import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileSpreadsheet, Loader2, Truck } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  downloadOrderJournalXlsx,
  fetchJournalPhotoUrl,
  getOrderJournal,
  type LineKey,
  type OrderJournal,
  type PalletView,
  type PhotoMeta,
} from "@/api/order_journal";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { blobErrorMessage } from "@/lib/api-errors";
import { fmtDate, fmtDateTime } from "@/lib/format";
import {
  LEGACY_TEXT,
  checkStatus,
  fmtQty,
  kindTitle,
  NO_UNIT_TEXT,
  palletNumberText,
  palletTitle,
  shipmentText,
} from "@/lib/order-journal";
import { LoadState, apiErrorText, useGoBack } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";
import { CodeSearch } from "@/pages/order-journal/CodeSearch";
import { EventsTab } from "@/pages/order-journal/EventsTab";
import { PalletCard } from "@/pages/order-journal/PalletCard";
import { ProductFilter } from "@/pages/order-journal/ProductFilter";
import { ReconciliationTab } from "@/pages/order-journal/ReconciliationTab";
import { StatusBadge, UrlCacheProvider } from "@/pages/order-journal/parts";

type Tab = "pallets" | "reconciliation" | "events";
const TABS: { key: Tab; title: string }[] = [
  { key: "pallets", title: "Паллеты" },
  { key: "reconciliation", title: "Сверка" },
  { key: "events", title: "События" },
];

/**
 * Журнал отгрузки по заказу: как собран и отгружен (read-only).
 *
 * Фильтр по товару и вкладка — в адресе (`?nom=&char=&partia=&tab=`): ссылку
 * на «паллеты с этим товаром» можно переслать тому, кто разбирает претензию.
 * Фильтр считает сервер; прошлый ответ остаётся на экране, пока идёт новый.
 * Сверка (единственный поход в 1С) и события грузятся только на своей вкладке,
 * сканы паллеты — по раскрытию, превью фото — когда карточка у экрана.
 */
export function OrderJournalPage(): React.ReactElement {
  const { orderRef = "" } = useParams<{ orderRef: string }>();
  const goBack = useGoBack("/order-journal");
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "pallets") as Tab;
  const nom = params.get("nom") ?? "";
  const filter: LineKey | null = nom
    ? {
        nomenclature_ref: nom,
        characteristic_ref: params.get("char") ?? "",
        partia_ref: params.get("partia") ?? "",
      }
    : null;

  const journalQ = useQuery({
    queryKey: ["order-journal", orderRef, filter],
    queryFn: () => getOrderJournal(orderRef, filter),
    placeholderData: keepPreviousData,
    enabled: Boolean(orderRef),
  });
  const j = journalQ.data;

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  const setFilter = (f: LineKey | null) =>
    update({
      nom: f?.nomenclature_ref ?? "",
      char: f?.characteristic_ref ?? "",
      partia: f?.partia_ref ?? "",
    });

  const [excelError, setExcelError] = useState("");
  const excelM = useMutation({
    mutationFn: () => downloadOrderJournalXlsx(orderRef),
    onMutate: () => setExcelError(""),
    onError: async (e) => setExcelError(await blobErrorMessage(e, "Не удалось выгрузить Excel")),
  });

  // Паллета, выбранная в поиске по коду: прокрутить к ней и выделить. Скрыта
  // фильтром по товару — фильтр снимаем, иначе прокручивать некуда.
  const [focusPallet, setFocusPallet] = useState("");
  // Прокрутка — один раз на выбор, а не на каждое обновление журнала.
  const scrollPending = useRef(false);
  const pickPallet = (id: string) => {
    setFocusPallet(id);
    scrollPending.current = true;
    const patch: Record<string, string> = { tab: "" };
    if (j && filter && !j.pallets.some((p) => p.id === id)) {
      Object.assign(patch, { nom: "", char: "", partia: "" });
    }
    update(patch);
  };
  useEffect(() => {
    if (!scrollPending.current || !focusPallet || tab !== "pallets") return;
    const el = document.getElementById(`pallet-${focusPallet}`);
    if (!el) return; // ещё грузится журнал без фильтра — дождёмся его
    scrollPending.current = false;
    el.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [focusPallet, tab, j]);

  const [lightbox, setLightbox] = useState<{ pallet: PalletView; index: number } | null>(null);
  const multiSession = (j?.sessions.length ?? 0) > 1;

  return (
    <UrlCacheProvider>
      <div className="mx-auto max-w-6xl space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={goBack}
            className="flex size-9 shrink-0 items-center justify-center rounded-md border hover:bg-accent"
            aria-label="К списку заказов"
          >
            <ArrowLeft className="size-4" />
          </button>
          <h1 className="min-w-0 flex-1 break-words text-xl font-semibold">
            Заказ {j?.header.order_number ?? ""}
            {j?.header.order_date && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                от {fmtDate(j.header.order_date)}
              </span>
            )}
          </h1>
          {j && (
            <Button
              variant="outline"
              size="sm"
              disabled={excelM.isPending}
              onClick={() => excelM.mutate()}
            >
              {excelM.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="size-4" />
              )}
              Excel
            </Button>
          )}
        </div>

        {excelError && (
          <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
            Excel не выгружен: {excelError}
          </div>
        )}

        {!j ? (
          <LoadState
            isLoading={journalQ.isLoading}
            error={journalQ.error}
            onRetry={() => void journalQ.refetch()}
            className="flex flex-col items-center justify-center gap-3 py-16 text-center"
          />
        ) : (
          <>
            <Header journal={j} />

            <Card className="space-y-3 p-3">
              <CodeSearch orderRef={orderRef} onPickPallet={pickPallet} />
              <ProductFilter options={j.filters} value={filter} onChange={setFilter} />
              {journalQ.isError && (
                <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
                  Журнал не обновился: {apiErrorText(journalQ.error)}
                </div>
              )}
            </Card>

            <div role="tablist" className="flex gap-1 border-b">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => update({ tab: t.key === "pallets" ? "" : t.key })}
                  className={cn(
                    "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
                    tab === t.key
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t.title}
                  {t.key === "pallets" && ` (${j.totals.pallets})`}
                </button>
              ))}
              {journalQ.isFetching && journalQ.isPlaceholderData && (
                <Loader2 className="ml-auto size-4 self-center animate-spin text-muted-foreground" />
              )}
            </div>

            {tab === "pallets" && (
              <PalletsTab
                journal={j}
                orderRef={orderRef}
                filtered={filter !== null}
                multiSession={multiSession}
                focusPallet={focusPallet}
                onOpenPhoto={(pallet, index) => setLightbox({ pallet, index })}
              />
            )}
            {tab === "reconciliation" && <ReconciliationTab orderRef={orderRef} />}
            {tab === "events" && <EventsTab orderRef={orderRef} showSession={multiSession} />}
          </>
        )}
      </div>

      {lightbox && j && (
        <PhotoLightbox<PhotoMeta>
          photos={lightbox.pallet.photos}
          index={lightbox.index}
          loadUrl={(p) => fetchJournalPhotoUrl(orderRef, p.id, false)}
          title={() => palletTitle(palletNumberText(lightbox.pallet), lightbox.pallet.label)}
          caption={(p) => `${fmtDateTime(p.created_at)} · снял: ${p.taken_by ?? LEGACY_TEXT}`}
          fileName={(p, i) =>
            `Паллета ${lightbox.pallet.sscc || lightbox.pallet.label || ""} · заказ ${j.header.order_number} (${i + 1}).${extOf(p.content_type)}`
          }
          onNavigate={(index) => setLightbox({ ...lightbox, index })}
          onClose={() => setLightbox(null)}
        />
      )}
    </UrlCacheProvider>
  );
}

function extOf(contentType: string): string {
  const sub = (contentType.split("/")[1] ?? "").toLowerCase();
  return sub === "jpeg" || !sub ? "jpg" : sub;
}

function Header({ journal }: { journal: OrderJournal }): React.ReactElement {
  const h = journal.header;
  const t = journal.totals;
  const external = journal.shipped_external.length - journal.shipments_without_unit;
  const withoutUnit = journal.pallets_without_unit + journal.shipments_without_unit;
  return (
    <Card className="space-y-3 p-3 text-sm sm:p-4">
      {withoutUnit > 0 && (
        <p data-testid="journal-without-unit" className="rounded-md bg-red-50 px-2 py-1 text-red-900">
          Без грузоместа: {withoutUnit} (паллет проверок — {journal.pallets_without_unit}, сканов
          отгрузки — {journal.shipments_without_unit}). Отгрузку к ним не приложить: импорт паллет
          из 1С не проставил грузоместо — сообщите администратору.
        </p>
      )}
      <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
        <Field label="Контрагент">{h.kontragent_name || "—"}</Field>
        <Field label="Организация">{h.organization_name || "—"}</Field>
        <Field label="Вид заказа">{kindTitle(h.kind)}</Field>
        <Field label={journal.applied_filter ? "Паллет с выбранным товаром" : "Паллет"}>
          {t.pallets}, отгружено {t.pallets_shipped}
          {external > 0 && ` · ещё ${external} отгружены вне портала`}
          <span className="text-muted-foreground"> · сканов {t.scans} · кол-во {fmtQty(t.qty_base)}</span>
        </Field>
        {h.comment && (
          <Field label="Комментарий" wide>
            <span className="break-words">{h.comment}</span>
          </Field>
        )}
      </div>
      <div>
        <div className="mb-1 text-xs text-muted-foreground">Проверки заказа</div>
        <ul className="space-y-1">
          {journal.sessions.map((s) => {
            const st = checkStatus(s.status);
            return (
              <li key={s.id} data-testid="journal-session" className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="font-medium">Проверка № {s.display_number ?? "—"}</span>
                <StatusBadge tone={st.tone}>{st.text}</StatusBadge>
                <span className="text-xs text-muted-foreground">
                  открыл {s.opened_by ?? "—"} · начата {fmtDateTime(s.created_at)}
                  {s.completed_at && ` · завершена ${fmtDateTime(s.completed_at)}`}
                  {s.expense_number ? ` · расходная № ${s.expense_number}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
}

function Field({
  label,
  wide,
  children,
}: {
  label: string;
  wide?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={cn("min-w-0", wide && "sm:col-span-2")}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="break-words">{children}</div>
    </div>
  );
}

function PalletsTab({
  journal,
  orderRef,
  filtered,
  multiSession,
  focusPallet,
  onOpenPhoto,
}: {
  journal: OrderJournal;
  orderRef: string;
  filtered: boolean;
  multiSession: boolean;
  focusPallet: string;
  onOpenPhoto: (pallet: PalletView, index: number) => void;
}): React.ReactElement {
  // Паллеты бота (грузоместо есть, в портале не собирались) и сканы отгрузки
  // БЕЗ грузоместа — разное: у вторых ключа нет вовсе, это не паллета бота.
  // Счёт — как в шапке: «вне портала» — только первые.
  const external = journal.shipped_external.filter((e) => e.cargo_unit_id !== null);
  const unitless = journal.shipped_external.filter((e) => e.cargo_unit_id === null);
  const hint = useMemo(
    () =>
      filtered
        ? `С выбранным товаром паллет: ${journal.totals.pallets} · кол-во ${fmtQty(journal.totals.qty_base)} · сканов ${journal.totals.scans}`
        : "",
    [filtered, journal.totals],
  );
  return (
    <div className="space-y-3">
      {hint && <p className="text-sm font-medium text-sky-900">{hint}</p>}
      {journal.pallets.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {filtered ? "Паллет с этим товаром нет" : "Паллет в проверках этого заказа нет"}
        </p>
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          {journal.pallets.map((p) => (
            <PalletCard
              key={p.id}
              orderRef={orderRef}
              pallet={p}
              filtered={filtered}
              showSession={multiSession}
              focused={focusPallet === p.id}
              onOpenPhoto={onOpenPhoto}
            />
          ))}
        </div>
      )}

      {external.length > 0 && (
        <Card className="space-y-2 p-3" data-testid="journal-external">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Truck className="size-4" /> Отгружены, но в портале не собирались ({external.length})
          </div>
          <p className="text-xs text-muted-foreground">
            Паллеты заказа прошли проверку отгрузки, но в проверке заказа в портале их нет
            (например, собраны по заданию бота): их состав есть только в 1С.
          </p>
          <ul className="divide-y text-sm">
            {external.map((e) => (
              <li key={e.id} className="py-1.5">
                <span className="font-medium">{palletTitle(palletNumberText(e), null)}</span>
                <span className="block break-words text-xs text-muted-foreground">
                  {shipmentText(e.shipment)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {unitless.length > 0 && (
        <Card className="space-y-2 border-red-200 p-3" data-testid="journal-external-unitless">
          <div className="flex items-center gap-2 text-sm font-semibold text-red-900">
            <Truck className="size-4" /> Сканы отгрузки без грузоместа ({unitless.length})
          </div>
          <p className="rounded-md bg-red-50 px-2 py-1 text-xs text-red-900">
            Отгрузку к паллете не приложить: импорт паллет из 1С не проставил грузоместо —
            сообщите администратору.
          </p>
          <ul className="divide-y text-sm">
            {unitless.map((e) => (
              <li key={e.id} className="py-1.5">
                <span className="font-medium">
                  {palletTitle(`${e.pallet_number} · ${NO_UNIT_TEXT}`, null)}
                </span>
                <span className="block break-words text-xs text-muted-foreground">
                  {shipmentText(e.shipment)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
